import React, { useEffect } from 'react';

import {
  promotionSelector,
  usePromotionStore,
} from '@editor/stores/promotionStore';
import { actionRetry } from '@editor/utils/actionRetry';
import { withEventScope } from '@repo/event-processor';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';
import { BasicModalProps, type ModalProps } from '@repo/ui/deprecated/Modal';

import { useUserStore, userStoreSelector } from '../../../stores/userStore';
import LoadingPlaceholder from '../LoadingPlaceholder/LoadingPlaceholder';
import ModalWrapper from '../ModalWrapper';

import { Wrapper } from './styles';
import SubscriptionSuccessContent from './SubscriptionSuccessContent';

const SubscriptionSuccessModal: React.FC<
  Required<BasicModalProps> &
    Pick<ModalProps, 'theme'> & { singlePayment?: boolean }
> = ({ isOpen, onClose, singlePayment, ...rest }) => {
  const user = useUserStore(userStoreSelector.user);
  const initUserStore = useUserStore(userStoreSelector.initialize);
  const isTeam = useUserStore(userStoreSelector.isTeam);
  const subscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );
  const { onClose: closeUpgradeModal } = usePromotionStore(
    promotionSelector.upgradeModalProps
  );

  const handleOnclose = (): void => {
    onClose();
    closeUpgradeModal();
  };

  useEffect(() => {
    if (user && !user.plan && isOpen) {
      initUserStore();
    }
  }, [user, initUserStore, isOpen]);

  // Sometimes it takes time before subscription is updated after payment, so polling is needed
  useEffect(() => {
    if (isOpen && (!subscription?.plan || !user?.plan)) {
      const pollUserInfo = async (): Promise<void> => {
        const result = await actionRetry(
          () => useUserStore.getState().updateUserInfo(),
          (_user) => (_user?.plan ? 'done' : 'retry'),
          { delay: 1500, attempts: 3 }
        );

        if (!result.success) {
          console.error('Load subscription exceeded allowed number of tries');
        }
      };
      pollUserInfo();
    }
  }, [isOpen, subscription?.plan, user?.plan]);

  if (!user || isTeam || !subscription?.plan) {
    return null;
  }

  return (
    <ModalWrapper
      isOpen={isOpen}
      width="980px"
      onClose={handleOnclose}
      {...rest}
    >
      <Wrapper data-testid="subscription--success-modal--content">
        {user.plan ? (
          <SubscriptionSuccessContent
            plan={user.plan}
            singlePayment={singlePayment}
            subscription={subscription}
            onContinueClick={handleOnclose}
          />
        ) : (
          <LoadingPlaceholder />
        )}
      </Wrapper>
    </ModalWrapper>
  );
};

export default withEventScope(SubscriptionSuccessModal, {
  feature: 'subscriptionSuccessModal',
});
